import { DefaultPassThrough } from '@primevue/core'
import { ButtonPassThroughOptions } from 'primevue/button'
import type { ButtonDesignTokens } from '@primeuix/themes/types/button'

export const ButtonPassthroughConfig: DefaultPassThrough<ButtonPassThroughOptions> = {
  root: {
    class: 'tw-button'
  }
}

export const ButtonStyleConfig: ButtonDesignTokens = {
  root: {
    borderRadius: 'var(--border-radius-sm)',
    gap: '0.5rem',
    paddingX: 'calc(var(--padding-md) - 1px)',
    paddingY: 'calc(var(--padding-sm) - 1px)',
    iconOnlyWidth: '2.25rem',
    sm: {
      fontSize: '0.875rem',
      iconOnlyWidth: '1.75rem',
      paddingX: 'calc(var(--padding-md) - 1px)',
      paddingY: 'calc(var(--padding-xs) - 1px)'
    },
    lg: {
      fontSize: '1rem',
      paddingX: 'calc(var(--padding-md) - 1px)',
      paddingY: 'calc(var(--padding-sm) - 1px)',
      iconOnlyWidth: '2.25rem'
    },
    label: {
      fontWeight: 'var(--button-font-weight)'
    },
    raisedShadow: '0px 1px 1px 0px rgba(219, 223, 223, 0.75)',
    focusRing: {
      width: '{focus.ring.width}',
      style: '{focus.ring.style}',
      offset: '{focus.ring.offset}'
    },
    badgeSize: '1rem',
    transitionDuration: '{form.field.transition.duration}'
  },
  colorScheme: {
    light: {
      root: {
        primary: {
          background: 'var(--color-primary)',
          hoverBackground: 'var(--action-primary-hover)',
          activeBackground: 'var(--action-primary-pressed)',
          borderColor: 'var(--color-primary)',
          hoverBorderColor: 'var(--action-primary-hover)',
          activeBorderColor: 'var(--action-primary-pressed)',
          color: '{primary.contrast.color}',
          hoverColor: '{primary.contrast.color}',
          activeColor: '{primary.contrast.color}',
          focusRing: {
            color: 'var(--color-primary)',
            shadow: 'transparent'
          }
        },
        secondary: {
          background: 'var(--action-secondary)',
          hoverBackground: 'var(--action-secondary-hover)',
          activeBackground: 'var(--action-secondary-pressed)',
          borderColor: 'var(--border-default-color)',
          hoverBorderColor: 'var(--border-default-color)',
          activeBorderColor: 'var(--border-default-color)',
          color: 'var(--color-text)',
          hoverColor: 'var(--color-text)',
          activeColor: 'var(--color-text)',
          focusRing: {
            color: 'var(--color-primary)',
            shadow: 'none'
          }
        },
        info: {
          background: '{sky.500}',
          hoverBackground: '{sky.600}',
          activeBackground: '{sky.700}',
          borderColor: '{sky.500}',
          hoverBorderColor: '{sky.600}',
          activeBorderColor: '{sky.700}',
          color: '#ffffff',
          hoverColor: '#ffffff',
          activeColor: '#ffffff',
          focusRing: {
            color: '{sky.500}',
            shadow: 'none'
          }
        },
        success: {
          background: '{green.500}',
          hoverBackground: '{green.600}',
          activeBackground: '{green.700}',
          borderColor: '{green.500}',
          hoverBorderColor: '{green.600}',
          activeBorderColor: '{green.700}',
          color: '#ffffff',
          hoverColor: '#ffffff',
          activeColor: '#ffffff',
          focusRing: {
            color: '{green.500}',
            shadow: 'none'
          }
        },
        warn: {
          background: '{orange.500}',
          hoverBackground: '{orange.600}',
          activeBackground: '{orange.700}',
          borderColor: '{orange.500}',
          hoverBorderColor: '{orange.600}',
          activeBorderColor: '{orange.700}',
          color: '#ffffff',
          hoverColor: '#ffffff',
          activeColor: '#ffffff',
          focusRing: {
            color: '{orange.500}',
            shadow: 'none'
          }
        },
        help: {
          background: '{purple.500}',
          hoverBackground: '{purple.600}',
          activeBackground: '{purple.700}',
          borderColor: '{purple.500}',
          hoverBorderColor: '{purple.600}',
          activeBorderColor: '{purple.700}',
          color: '#ffffff',
          hoverColor: '#ffffff',
          activeColor: '#ffffff',
          focusRing: {
            color: '{purple.500}',
            shadow: 'none'
          }
        },
        danger: {
          background: 'var(--action-critical)',
          hoverBackground: 'var(--action-critical-hover)',
          activeBackground: 'var(--action-critical-pressed)',
          borderColor: 'var(--action-critical)',
          hoverBorderColor: 'var(--action-critical-hover)',
          activeBorderColor: 'var(--action-critical-pressed)',
          color: '#ffffff',
          hoverColor: '#ffffff',
          activeColor: '#ffffff',
          focusRing: {
            color: 'var(--action-critical)',
            shadow: 'none'
          }
        },
        contrast: {
          background: '{surface.950}',
          hoverBackground: '{surface.900}',
          activeBackground: '{surface.800}',
          borderColor: '{surface.950}',
          hoverBorderColor: '{surface.900}',
          activeBorderColor: '{surface.800}',
          color: '{surface.0}',
          hoverColor: '{surface.0}',
          activeColor: '{surface.0}',
          focusRing: {
            color: '{surface.950}',
            shadow: 'none'
          }
        }
      },
      outlined: {
        primary: {
          hoverBackground: '{primary.50}',
          activeBackground: '{primary.100}',
          borderColor: '{primary.200}',
          color: '{primary.color}'
        },
        secondary: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          borderColor: '{surface.200}',
          color: '{surface.500}'
        },
        success: {
          hoverBackground: '{green.50}',
          activeBackground: '{green.100}',
          borderColor: '{green.200}',
          color: '{green.500}'
        },
        info: {
          hoverBackground: '{sky.50}',
          activeBackground: '{sky.100}',
          borderColor: '{sky.200}',
          color: '{sky.500}'
        },
        warn: {
          hoverBackground: '{orange.50}',
          activeBackground: '{orange.100}',
          borderColor: '{orange.200}',
          color: '{orange.500}'
        },
        help: {
          hoverBackground: '{purple.50}',
          activeBackground: '{purple.100}',
          borderColor: '{purple.200}',
          color: '{purple.500}'
        },
        danger: {
          hoverBackground: '{red.50}',
          activeBackground: '{red.100}',
          borderColor: '{red.200}',
          color: '{red.500}'
        },
        contrast: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          borderColor: '{surface.700}',
          color: '{surface.950}'
        },
        plain: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          borderColor: '{surface.200}',
          color: '{surface.700}'
        }
      },
      text: {
        primary: {
          hoverBackground: 'var(--color-transparent)',
          activeBackground: 'var(--color-transparent)',
          color: 'var(--color-text)'
        },
        secondary: {
          hoverBackground: 'var(--action-secondary-hover)',
          color: 'var(--color-primary)'
        },
        success: {
          hoverBackground: '{green.50}',
          activeBackground: '{green.100}',
          color: '{green.500}'
        },
        info: {
          hoverBackground: '{sky.50}',
          activeBackground: '{sky.100}',
          color: '{sky.500}'
        },
        warn: {
          hoverBackground: '{orange.50}',
          activeBackground: '{orange.100}',
          color: '{orange.500}'
        },
        help: {
          hoverBackground: '{purple.50}',
          activeBackground: '{purple.100}',
          color: '{purple.500}'
        },
        danger: {
          hoverBackground: '{red.50}',
          activeBackground: '{red.100}',
          color: '{red.500}'
        },
        plain: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          color: '{surface.700}'
        }
      },
      link: {
        color: '{primary.color}',
        hoverColor: '{primary.color}',
        activeColor: '{primary.color}'
      }
    }
  }
}
